//////////////////////////////////////////////////////////////////////////////
// GIS Air Traffic Monitoring Demo: an example of using the GLG Map Server
// integrated in the GLG drawing.
//
// The demo is written in pure HTML5 and JavaScript. The source code of the
// demo uses the GLG Toolkit JavaScript Library supplied by the included
// Glg*.js and GlgToolkit*.js files. The GLG library loads a GLG drawing
// and renders it on a web page, providing an API to handle user interaction
// with graphical objects in the drawing.
//
// The drawings are created using the GLG Graphics Builder, an interactive
// editor that allows to create grahical objects and define their dynamic
// behavior without any programming. The airplane icons are dynamic
// GLG objects created with the Graphics Builder.
//
// The background map is generated by the GLG Map Server, which is integrated
// inside of a GLG drawing as a GLG GIS Object. The GIS Object takes a
// complete care of the map display, automatically generating Map Server
// requests to re-generate the image when the map is panned or zoomed.
//
// The demo uses asyncronous map loading: when the map is zoomed or scrolled,
// the drawing is changed right away, while the new map will appear when it's
// loaded. The GLG Air Traffic Control Demo demonstrates an alternative
// approach that uses an asynchronous map loading request to load a map:
// when the map is zoomed or scrolled, the program will continue to operate
// in the old zoom or pan state and will switch to the new zoom or pan state
// when the new map is ready. 
//
// Except for the changes to comply with the JavaScript syntax, this source
// is identical to the source code of the corresponding C/C++/C# and Java
// desktop versions of the demo.
//
// This demo uses the GLG Map Server to display a map.
// The Map Server has to be installed either on the local host or on a
// remote web server. After the Map Server has been installed, modify the
// source code to set the SuppliedMapServerURL variable to point to the
// Map Server location.
//////////////////////////////////////////////////////////////////////////////

/* eslint eqeqeq: 0 */

import { GlgToolkit } from '../GlgToolkitDemo.mod.js'

// Enable general debugging/diagnostics information.
const DEBUG = false;

/* Debugging: set the variable to true to throw an exception on a GLG error
   instead of just displaying an error message on the console.
*/
const DEBUG_GLG_ERRORS = false;

/* This demo uses the GLG Map Server to display a map.
   The Map Server has to be installed either on the local host or on a
   remote web server. After the Map Server has been installed, set
   SuppliedMapServerURL to point to the Map Server location:

   For example on Linux:
     SuppliedMapServerURL = "http://localhost/cgi-bin/GlmScript";
   or
     SuppliedMapServerURL = "http://www.myserver.com/cgi-bin/GlmScript";

   On Windows:
     SuppliedMapServerURL = "http://localhost/Scripts/GlmScript.pl";
   or
     SuppliedMapServerURL = "http://www.myserver.com/Scripts/GlmScript.pl";

   The SuppliedMapServerURL value overrides the URL of the GIS object in the
   drawing.
*/
const SuppliedMapServerURL = null;    /* String */

const UPDATE_INTERVAL = 50; // Update interval in msec.

const US_ZOOM_DELAY1 = 2000;  // Delay to zoom to the US area to show details.
const US_ZOOM_DELAY2 = 2000;  // Delay to remove US zooming message.

/* Controls the plane speed (in relative coordinates) used to simulate moving
   airplanes.
*/
const PLANE_SPEED_DESKTOP = 0.001;    // Desktop version
const PLANE_SPEED_MOBILE = 0.005;     // Mobile version

/* Controls the plane speed (in XY coordinates) used to simulate moving
   airplanes in the zoomed state.
*/
const MAX_ZOOM_SPEED = 5.0;

/* Scale factors used for changing airplane sizes. */
const
   SMALL_SIZE  = 0.23,
   MEDIUM_SIZE = 0.33,
   BIG_SIZE =    0.43;

// Global handle to the GLG Toolkit library.
let GLG = new GlgToolkit();

//////////////////////////////////////////////////////////////////////////////
// Creates an instance of the GIS demo.
// Parameters:
//   glg_div_name  - name of parent div the drawing will be displayed in,
//                   will be passed by the caller.
//   is_mobile     - true if deployed on mobile devices.
//   is_standalone - true if deployed in html, false if deployed in react or
//                   angular.
//   glg_path      - path to the directory where GLG drawings are located.
//////////////////////////////////////////////////////////////////////////////
export function GlgGIS( glg_div_name, glg_path, is_standalone, is_mobile )
{
   Debug( "New script for " + glg_div_name + ": "  +
          Object.getPrototypeOf( this ).constructor.name +
          " glg_path: " + glg_path );
   
   if( DEBUG_GLG_ERRORS )   
     GLG.ThrowExceptionOnError( true, true, true );
   else
     GLG.ThrowExceptionOnError( false, false, false );

   this.GLG_div_name = glg_div_name;
   this.IsMobile = is_mobile;
   this.Standalone = is_standalone;    

   // Use path to the drawings directory is supplied.
   this.GlgPath = glg_path;

   /* Defines an action to perform on touch on mobile devices: 
      select an airplane or scroll the map. On mobile devices, set to false
      to select a plane when it is touched by default. This may be changed
      using the Touch Action buttons.
   */
   this.ScrollMode = ( is_mobile ? false : true );

   /* Array of icons to place on the map as GLG objects in addition to the icons
      defined in GIS server's data. The icons that use GLG objects may be 
      selected with the mouse and their attributes can be changed dynamically, 
      based on data. When the mouse moves over an icon, it may be highlighted 
      with a different color or a tooltip may be displayed. 
   */
   this.NodeArray =
     [ new NodeData( "Boston",        -71.01789,  42.33602 ),
       new NodeData( "New York",      -73.97213,  40.77436 ),
       new NodeData( "San Francisco", -122.55478, 37.79325 ),
       new NodeData( "Miami",         -80.21084,  25.77566 ),
       new NodeData( "Seattle",       -122.35032, 47.62180 ),
       new NodeData( "Houston",       -95.38672,  29.76870 ),
       new NodeData( "Denver",        -104.87265, 39.76803 ),
       new NodeData( "Minneapolis",   -93.26684,  44.96185 ),
       new NodeData( "Chicago",       -87.68496,  41.83705 ),
       new NodeData( "Dallas",        -96.76524,  32.79415 ) ];

   this.NumNodes = this.NodeArray.length;  /* Number of airports */
   this.NumPlanes = 10;                 /* Number of simulated planes */    

   this.PlaneSpeed = ( is_mobile ? PLANE_SPEED_MOBILE : PLANE_SPEED_DESKTOP );

   this.Drawing = null;                 /* GlgObject */
   this.PositionObject = null;          /* GlgObject */
   this.PositionArea = null;            /* GlgObject */

   this.NodeTemplate = new Array( 2 );  /* GlgObject[2] */
   this.PlaneTemplate = new Array( 2 ); /* GlgObject[2] */
   this.Map = new Array( 2 );           /* GlgObject[2] */
   this.GISObject = new Array( 2 );     /* GlgObject[2] */
   this.NodeGroup = new Array( 2 );     /* GlgObject[2] */
   this.PlaneGroup = new Array( 2 );    /* GlgObject[2] */
   this.MapProjection = new Array( 2 ); /* int[2] */
   this.MapLoaded = new Array( 2 );     /* boolean[2] */

   // Stores initial extent and center, used to reset the map.
   this.InitExtent = new Array( 2 );    /* GlgPoint[2] */
   this.InitCenter = new Array( 2 );    /* GlgPoint[2] */

   this.PlaneSize = MEDIUM_SIZE;        /* double */

   /* If true, pan the map to make the selected plane visible in the current
      zoomed area when it moves out of the area.
   */
   this.LockSelectedPlane = false;     /* boolean */
   this.CityLabels = true;             /* boolean */
   this.StateDisplay = true;           /* boolean */
   this.SuspendPromptUpdates = false;  /* boolean */
   this.PanMode = false;               /* boolean */
   this.PerformUpdates = true;         /* boolean */
   this.DraggingFromButton = false;    /* boolean */

   // Temporary variables: allocate once
   this.lat_lon = GLG.CreateGlgPoint( 0, 0, 0 );          /* GlgPoint */
   this.position = GLG.CreateGlgPoint( 0, 0, 0 );         /* GlgPoint */
   this.old_position = GLG.CreateGlgPoint( 0, 0, 0 );     /* GlgPoint */
   this.new_position = GLG.CreateGlgPoint( 0, 0, 0 );     /* GlgPoint */
   this.last_xyz = GLG.CreateGlgPoint( 0, 0, 0 );         /* GlgPoint */
   this.curr_xyz = GLG.CreateGlgPoint( 0, 0, 0 );         /* GlgPoint */
   this.point = GLG.CreateGlgPoint( 0, 0, 0 );            /* GlgPoint */
   this.util_point = GLG.CreateGlgPoint( 0, 0, 0 );       /* GlgPoint */
   this.selection_rect = GLG.CreateGlgCube( null, null ); /* GlgCube */

   this.PlaneArray = null;             /* PlaneData[] */
   this.SelectedPlane = null;          /* PlaneData */

   /* Coefficient for canvas resolution. It will be adjusted in 
      SetCanvasResolution() for mobile devices with HiDPI displays as well as 
      on browser zoom.
   */
   this.CoordScale = 1;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Starts GIS demo by loading its drawing.
////////////////////////////////////////////////////////////////////////////// 
GlgGIS.prototype.Start = function()
{
   Debug( "Starting: " + this.GLG_div_name );

   this.Active = true;
   
   // Set initial size of the drawing.
   this.SetDrawingSize( false );

   // Show touch action selector for mobile devices.
   if( this.IsMobile )
     ShowElement( this.GLG_div_name + "_touch_action", true );

   /* Load misc. assets such as GLG scrollbars. When assets are loaded, 
      LoadDrawing callback is invoked that loads a GLG drawing defined by
      DrawingName variable.
   */
   this.LoadAssets( ()=>this.LoadDrawing(), null );
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Performs cleanup.
////////////////////////////////////////////////////////////////////////////// 
GlgGIS.prototype.Cleanup = function()
{
   Debug( "Cleanup for: " + this.GLG_div_name );
   
   this.Active = false;    // Ignore any pending updates and callbacks.

   if( this.Drawing )
     this.Drawing.ResetHierarchy();   // Undisplay GLG drawing.

   if( this.ResizeListener )
     window.removeEventListener( "resize", this.ResizeListener );
}

////////////////////////////////////////////////////////////////////////////// 
// Load a GLG drawing from a file.
////////////////////////////////////////////////////////////////////////////// 
GlgGIS.prototype.LoadDrawing = function()
{
   Debug( "LoadDrawing for: " + this.GLG_div_name );
   
   if( !this.Active )
     return;

   /* Load a drawing from the gis_demo.g file. 
      The LoadCB callback will be invoked when the drawing has been loaded.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   let drawing_file = ( this.IsMobile ? "gis_demo2.g" : "gis_demo.g" );   

   GLG.LoadWidgetFromURL( this.GetFullName( drawing_file ), null,
                          this.LoadCB.bind( this ), /*user data*/ null,
                          /*abort test function*/ ()=>!this.Active );
}

////////////////////////////////////////////////////////////////////////////// 
GlgGIS.prototype.GetFullName = function( drawing_name )
{
   if( this.GlgPath == null )
     return drawing_name;

   return this.GlgPath + "/" + drawing_name;
}

//////////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.LoadCB =
  function( /*GlgObject*/ drawing, /*Object*/ user_data, /*String*/ path )
{
   Debug( "LoadCB for: " + this.GLG_div_name );

   if( !this.Active )
     return;

   if( drawing == null )
   {
      AppAlert( "Can't load drawing, check console message for details." );
      return;
   }
   
   if( !document.getElementById( this.GLG_div_name ) )
   {
      Debug( "Can't find " + this.GLG_div_name +
             " div: it may have been removed from the document." );
      return;
   }
    
   // Disable spinning loader.   
   RemoveElement( this.GLG_div_name, "loader_container" );
    
   // Define the element in the HTML page to display the drawing.
   drawing.SetParentElement( this.GLG_div_name );
    
   // Disable viewport border to use the border of the glg_area.
   if( this.Standalone )
     drawing.SetDResource( "LineWidth", 0 );
    
   this.StartGISDemo( drawing );
}

//////////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.StartGISDemo = function( drawing )
{
   this.Drawing = drawing;

   this.InitDrawing();
   this.InitIcons();

   this.Drawing.SetupHierarchy();

   /* Adjust selected region on the thumbnail map to match the zoomed area 
      of the detailed map.
   */
   this.SetSelectedArea();

   this.SetMapLoading( 0, true );
   this.SetMapLoading( 1, true );

   this.Drawing.Update();
   
   // Start animation.
   setTimeout( ()=>this.UpdatePlanes(), UPDATE_INTERVAL );

   // Zoom to the US area after a few seconds to show details.        
   setTimeout( ()=>this.ZoomToUSStart(), US_ZOOM_DELAY1 );
}

//////////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.InitDrawing = function()
{
   // Show mouse over tooltips right away.
   this.Drawing.SetDResource( "$config/GlgMouseTooltipTimeout", 0.05 );

   this.Map[ 0 ] = this.Drawing.GetResourceObject( "TopMap" ); // Thumbnail map
   this.Map[ 1 ] = this.Drawing.GetResourceObject( "Map" );    // Detailed map

   /* Display thumbnail map in a floating dialog at run time. It is kept 
      as a child viewport in the drawing for the convinience of editing.
   */
   this.Map[ 0 ].SetDResource( "ShellType", GLG.GlgShellType.DIALOG_SHELL );
   this.Map[ 0 ].SetSResource( "Screen/ScreenName", "Globe View" );

   // Set an initial position of the thumbnail map dialog.
   if( this.IsMobile )
     this.Map[ 0 ].SetGResource( "Position", 800, -1600, 0 );
   else
     this.Map[ 0 ].SetGResource( "Position", 50, -1120, 0 );        
   
   for( let i=0; i<2; ++i )   // For each map window
   {
      // Get IDs of the GIS objects in each of the map viewports. 
      this.GISObject[ i ] = this.Map[ i ].GetResourceObject( "GISObject" );

      if( SuppliedMapServerURL != null )
        // Override the URL defined in the drawing.
        this.GISObject[ i ].SetSResource( "GISMapServerURL",
                                          SuppliedMapServerURL );

      /* Query and store the GIS projection (ORTHOGRAPHIC or RECTANGULAR)
         used to render the map.
      */
      this.MapProjection[ i ] =
        Math.trunc( this.GISObject[ i ].GetDResource( "GISProjection" ) );

      /* Set the GIS Zoom mode. It was set and saved with the drawing, 
         but do it again programmatically just in case.
      */
      this.Map[ i ].SetZoomMode( null, this.GISObject[ i ], null, 
                                 GLG.GlgZoomMode.GIS_ZOOM_MODE );

      // Store initial map extent for resetting after zooming.
      this.InitExtent[i] = this.GISObject[ i ].GetGResource( "GISExtent" );
      this.InitCenter[i] = this.GISObject[ i ].GetGResource( "GISCenter" );
   }

   /* Add Input and Trace callbacks used to handle user interaction. */
   this.Drawing.AddListener( GLG.GlgCallbackType.INPUT_CB,
                             this.InputCallback.bind( this ) );
   this.Drawing.AddListener( GLG.GlgCallbackType.TRACE_CB,
                             this.TraceCallback.bind( this ) );
}

//////////////////////////////////////////////////////////////////////////
// Initializes icons in the drawing
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.InitIcons = function()
{
   // Get the palette containing templates for plane and node icons.
   let palette = this.Drawing.GetResourceObject( "Palette" );

   // Delete it from the drawing
   this.Drawing.DeleteThisObject( palette );

   /* Get node and plane templates from the palette. Two sets of templates
      are used: smaller icons for the thumbnail view and more elaborate 
      ones for the detailed map.
   */
   for( let i=0; i<2; ++i )
   {
      this.NodeTemplate[ i ] =
        palette.GetResourceObject( i == 0 ? "Node1" : "Node2" );

      this.PlaneTemplate[ i ] = 
        palette.GetResourceObject( i == 0 ? "Plane1" : "Plane2" );

      /* If the icon is not a marker (IconScale resource exists), set the
         icon's size.
      */
      let resource = this.PlaneTemplate[ i ].GetResourceObject( "IconScale" );
      if( resource != null )
        resource.SetDResource( null, this.PlaneSize );
   }

   // Create and initialize plane structures used for simulation.
   this.PlaneArray = new Array( this.NumPlanes );
   for( let i=0; i < this.NumPlanes; ++i )
   {
      this.PlaneArray[ i ] = new PlaneData();
      this.PlaneArray[ i ].name = i.toString();
   }
      
   // Add node and plane icons to both thumbnail and detailed map.
   for( let i=0; i<2; ++i )
   {	 
      this.CreateAirportIcons( i ); // Add airport icons
      this.CreatePlaneIcons( i );   // Add plane icons
   }

   // Start all planes.
   for( let i=0; i < this.NumPlanes; ++i )
     this.StartPlane( this.PlaneArray[ i ], true );

   /* Selected area annotates the currently viewed area of the detailed map 
      in the thumbnail map view. Reorder SelectedArea on top of icons
      (last in the array).
   */
   let selected_area =   /* GlgObject */
     this.GISObject[ 0 ].GetResourceObject( "GISArray/SelectedArea" );
   this.GISObject[ 0 ].ReorderElement( this.GISObject[ 0 ].GetIndex( selected_area ),
                                       this.GISObject[ 0 ].GetSize() - 1 );

   /* Set state display on the thumbnail map. Airport labels on the detailed
      map are handled by HandleZoomLevel(). 
   */
   this.SetGISLayers( 0 );

   /* Demos starts with the whole word view, then zooms to the US area
      in a few seconds to show more details. Set initial parameters
      for the whole world view.
   */
   this.HandleZoomLevel();

   this.InitSelection();  // Select some plane.

   // Store objects used to display lat/lon on mouse move.
   this.PositionArea = this.Drawing.GetResourceObject( "PositionArea" );
   this.PositionObject =
     this.Drawing.GetResourceObject( "PositionLabel/PointerLatLon" );
   this.PositionObject.SetSResource( null, "" );

   // Set US zooming message to OFF initially.
   this.Drawing.SetDResource( "Map/USZoomingMessage/Visibility", 0.0 );
}

//////////////////////////////////////////////////////////////////////////
// Creates NodeGroup to hold airport icons and adds the icons to it.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.CreateAirportIcons = function( /* int */ map )
{
   this.NodeGroup[ map ] =
     GLG.CreateObject( GLG.GlgObjectType.ARRAY,
                       GLG.GlgContainerType.GLG_OBJECT, 0, 0, null );
   this.NodeGroup[ map ].SetSResource( "Name", "NodeGroup" );

   // Add city/node icons
   for( let i = 0; i < this.NumNodes; ++i )
     this.AddNode( this.NodeArray[ i ], map, i );

   /* Add the group to the GIS Object which will automatically manage 
      the GIS coordinate conversion. This allows to specify node 
      positions in lat/lon instead of the X/Y world coordinates.
   */
   this.GISObject[ map ].AddObjectToBottom( this.NodeGroup[ map ] );
}

//////////////////////////////////////////////////////////////////////////
// Creates PlaneGroup to hold plane icons and adds the icons to it.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.CreatePlaneIcons = function( /* int */ map )
{
   this.PlaneGroup[ map ] =
     GLG.CreateObject( GLG.GlgObjectType.ARRAY,
                       GLG.GlgContainerType.GLG_OBJECT, 0, 0, null );
   this.PlaneGroup[ map ].SetSResource( "Name", "PlaneGroup" );

   // Add plane icons
   for( let i=0; i < this.NumPlanes; ++i )
     this.AddPlane( this.PlaneArray[ i ], map, i );
   
   /* Add the group to the GIS Object which will automatically manage 
      the GIS coordinate conversion. This allows to specify plane 
      positions in lat/lon instead of the X/Y world coordinates.
   */
   this.GISObject[ map ].AddObjectToBottom( this.PlaneGroup[ map ] );
}

//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.AddNode =
  function( /* NodeData */ node_data, /* int */ map, /* int */ index )
{      
   // Create a copy of a node (GlgObject). */
   let node =
     this.NodeTemplate[ map ].CloneObject( GLG.GlgCloneType.STRONG_CLONE );
      
   node.SetSResource( "Name", node_data.name );  // Set object name

   /* Set node position in lat/lon coordinates. The GIS object will handle
      the GIS coordinate conversion depending on the map the node icon is 
      displayed in, as well as the map's zoom and pan state.
   */
   node.SetGResource( "Position", 
                      node_data.lat_lon.x, node_data.lat_lon.y, 0.0 );

   // Index for direct access
   node.SetDResource( "DataIndex", index );

   if( map == 1 )        // On the detailed map, show node name label.
     node.SetSResource( "LabelString", node_data.name );
      
   let tooltip;
   if( map == 0 )   // On the thumbnail map, show node name in the tooltip.
     tooltip = node_data.name;
   else   // On the detailed map, include lat/lon into the tooltip.
     tooltip =
       node_data.name + ", " + CreateLocationString( node_data.lat_lon );
   node.SetSResource( "TooltipString", tooltip );
        
   node_data.graphics[ map ] = node;

   // Add the node to the requested map (thumbnail or detailed).
   this.NodeGroup[ map ].AddObjectToBottom( node );
}

//////////////////////////////////////////////////////////////////////////
// Adds a plane icon, fills labels, tooltips, etc.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.AddPlane =
  function( /* PlaneData */ plane_data, /* int */ map, /* int */ index )
{
   // Create a copy of a plane (GlgObject).
   let plane =
     this.PlaneTemplate[ map ].CloneObject( GLG.GlgCloneType.STRONG_CLONE );
   
   plane.SetSResource( "Name", plane_data.name );      // Object name
   
   // Index for direct access
   plane.SetDResource( "DataIndex", index );
   
   plane_data.graphics[ map ] = plane;
   
   // Check if the icon has an angle to indicate its direction.
   if( plane.GetResourceObject( "Angle" ) != null )
     plane_data.has_angle[ map ] = true;
   
   /* The plane will be positioned with PositionPlane() after the GIS object
      have been setup and the plane's lat/lon has been calculated by the 
      flight simulation. */

   // Add the plane to the requested map (thumbnail or detailed).
   this.PlaneGroup[ map ].AddObjectToBottom( plane );
}

//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.PositionPlane =
  function( /* PlaneData */ plane, /* int */ map )
{
   if( plane.graphics[ map ] == null || 
       plane.from_node == null || plane.to_node == null )
     return;
   
   // Obtain the plane's current position.
   GetPlaneLatLon( plane );

   /* Update plane's icon in the drawing by setting its lat/lon coordinates.
      The GIS object will handle the GIS coordinate conversion depending on 
      the map the plane icon is displayed in, as well as the map's zoom 
      and pan state.
   */
   plane.graphics[ map ].SetGResource( "Position",
                                       plane.lat_lon.x, plane.lat_lon.y, 0.0 );

   // Update icon's direction angle is necessary
   if( plane.has_angle[ map ] )
   {
      let angle = this.GetPlaneAngle( plane, map );   /* double  */
      plane.graphics[ map ].SetDResource( "Angle", angle );
   }
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Converts plane lat/lon to the GLG world 
//     coordinates for calculating plane speed and directional angle.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.GetPlanePosition =
  function( /* PlaneData */ plane, /* int */ map, /* GlgPoint */ xyz )
{
   // Converts lat/lon to X/Y using GIS object's current projection.
   this.GISObject[ map ].GISConvert( null, GLG.GlgCoordType.OBJECT_COORD, 
                                     /* Lat/Lon to XY */ false,
                                     plane.lat_lon, xyz );
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Converts node lat/lon to the GLG world 
//     coordinates for calculating plane's initial directional angle.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.GetNodePosition =
  function( /* NodeData */ node, /* int */ map, /* GlgPoint */ xyz )
{
   // Converts lat/lon to X/Y using GIS object's current projection.
   this.GISObject[ map ].GISConvert( null, GLG.GlgCoordType.OBJECT_COORD, 
                                     /* Lat/Lon to XY */ false,
                                     node.lat_lon, xyz );
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Calculates plane icon's directional angle.
//     In an application, it will query the plane's directional angle.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.GetPlaneAngle =
  function( /* PlaneData */ plane, /* int */ map )  /* double */
{
   /* Rectangular projection preserves straight lines, we can use the 
      angle of the line connecting the start and end nodes. For the
      orthographic projection, use this case if the plane has just started
      and there is no previous position stored.
   */
   if( this.MapProjection[ map ] == GLG.GlgProjectionType.RECTANGULAR_PROJECTION ||
       plane.path_position == plane.path_position_last )   // Just started
   {
      this.GetNodePosition( plane.from_node, map, this.last_xyz );
      this.GetNodePosition( plane.to_node, map, this.curr_xyz );
   }
   else  /* In the orthographic projection straight lines are drawn as 
            curves. Use the angle of the line connecting the current and 
            last position of the plane. */
   {
      let stored_position;   /* double */
      
      stored_position = plane.path_position;    // Store current position.
      
      // Get coordinates of the plane's previous position
      plane.path_position = plane.path_position_last;
      GetPlaneLatLon( plane );
      this.GetPlanePosition( plane, map, this.last_xyz );
      
      // Restore the plane's current position and get its coordinates.
      plane.path_position = stored_position;
      GetPlaneLatLon( plane );
      this.GetPlanePosition( plane, map, this.curr_xyz );
   }

   /* Calculate the angle of a line connecting the previous and 
      current position.
   */
   return GetAngle( this.last_xyz, this.curr_xyz );
}

//////////////////////////////////////////////////////////////////////////
// Checks if the object is visible in the current zoom region.
// This prevents wrap-around errors under big zoom factors.
//////////////////////////////////////////////////////////////////////////
function GetVisibility( /* GlgPoint */ position,
                        /* double */ adj )   /* boolean */
{
   // Use adj as a gap
   return ( position.x > -1000.0 * adj && position.x < 1000.0 * adj &&
            position.y > -1000.0 * adj && position.y < 1000.0 * adj );
}

//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetMapLoading = function( /* int */ map, /* boolean */ show )
{
   this.Map[ map ].SetDResource( "LoadingMessage/Visibility", show ? 1 : 0 );
   this.MapLoaded[ map ] = !show; // Set to false when the message is displayed.
}

//////////////////////////////////////////////////////////////////////////
// Updates plane positions on both maps.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.UpdatePlanes = function()
{
   if( !this.Active )
     return;
   
   for( let i=0; i<2; ++i )
     if( !this.MapLoaded[ i ] &&
         Math.trunc( this.GISObject[ i ].GetDResource( "ImageLoaded" ) ) != 0 )
       this.SetMapLoading( i, false );
   
   if( this.PerformUpdates )
   {
      for( let i = 0; i < this.NumPlanes; ++i )
        this.UpdatePlane( this.PlaneArray[ i ] );
      
      this.UpdateSelectedPlaneStatus();
      
      if( this.LockSelectedPlane )
        this.UpdateLocking();
      
      this.Drawing.Update();
   }
   
   // Restart update timer
   setTimeout( ()=>this.UpdatePlanes(), UPDATE_INTERVAL );
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Calculates new plane position using a
//     flight simulation. In an application, the real plane position 
//     will be queried here.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.UpdatePlane = function( /* PlaneData */ plane )
{      
   if( plane.graphics == null ||
       plane.from_node == null || plane.to_node == null )
     return;
   
   if( plane.path_position == 1.0 )
     this.StartPlane( plane, false );   // Finished old path, start a new one. 
   else
   {
      let speed = this.PlaneSpeed;   /* double */
      
      /* Slow the selected plane down when zoomed on it for a nice
         demo effect.
      */
      if( plane == this.SelectedPlane && this.LockSelectedPlane )
      {
         this.GetPlanePosition( plane, 1, this.old_position );
         
         // Store the current path position.
         let stored_position = plane.path_position;   /* double */

         plane.path_position += plane.speed * speed;    // Increment it
         
         this.GetPlanePosition( plane, 1, this.new_position );
         
         // Distance between the old and current position of the plane
         let dist =
           GetLength( this.old_position, this.new_position );   /* double */

         // Adjust the plane's speed to slow it down if the distance is too big.
         if( dist > MAX_ZOOM_SPEED )
         {
            let slow_down = dist / MAX_ZOOM_SPEED;   /* double */
            speed /= slow_down;
         }

         // Restore the current path position.
         plane.path_position = stored_position;
      }
      
      /* Store last position for calculating the angle in the ORTHO 
         projection.
      */
      plane.path_position_last = plane.path_position;
      
      // Move the plane.
      plane.path_position += plane.speed * speed;
      if( plane.path_position > 1.0 )
        plane.path_position = 1.0; // Clamp to 1: can't go past the airport!
   }
   
   for( let i =0; i<2; ++i )
     this.PositionPlane( plane, i );    // Position the plane on both maps
}

//////////////////////////////////////////////////////////////////////////
// In the lock mode, pans the map to keep selected plane visible when the 
// plane moves out of the detailed map area.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.UpdateLocking = function()
{	 
   // Locking is in effect only on the detailed map.
   let map = 1;

   this.GetPlanePosition( this.SelectedPlane, map, this.position );
   
   /* If selected plane goes on another side of the globe or off the 
      visible portion of the map, pan the map to re-center on the 
      selected plane. The Z coordinate goes to zero when the plane gets 
      close to the globe's edge, and becomes negative on the invisible 
      side of the globe.
   */
   if( ( this.MapProjection[ map ] == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION &&
         this.position.z < 0.1 ) || !GetVisibility( this.position, 0.9 ) )
   {
      this.CenterOnPlane( this.SelectedPlane, 1 );
      this.UpdateMap( 1 );
      
      /* If the thumbnail map used orthographic projection, rotate the 
         thumbnail globe to keep the selected plane visible. In rectangular
         projection, the whole world is visible and no action is required.
      */
      if( this.MapProjection[ map ] == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
      {
         this.CenterOnPlane( this.SelectedPlane, 0 );
         this.UpdateMap( 0 );
      }
   }
}

//////////////////////////////////////////////////////////////////////////
// Updates the map image; also updates the selected region display on the
// thumbnail map.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.UpdateMap = function( /* int */ map )
{
   // Display the wait message while the new map image is being generated.
   this.SetMapLoading( map, true );
   
   /* Just setup, don't draw: will be done at the end by the caller
      after selected area, etc., was updated.
   */
   this.Map[ map ].SetupHierarchy();
   
   /* Adjust selected region on the thumbnail map to match the new 
      zoom area of the detailed map.
   */
   this.SetSelectedArea();
}

//////////////////////////////////////////////////////////////////////////
// Handle user interaction.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.InputCallback =
  function( /* GlgObject */ vp, /* GlgObject */ message_obj )
{
   if( !this.Active )
     return;

   let origin = message_obj.GetSResource( "Origin" );
   let format = message_obj.GetSResource( "Format" );
   let action = message_obj.GetSResource( "Action" );
   let subaction = message_obj.GetSResource( "SubAction" );

   // Handle window closing.
   if( format == "Window" )
   {
      if( action == "DeleteWindow" )
      {
         if( origin == "SelectionDialog" )
         {
            // Close selection dialog
            this.Drawing.SetDResource( "SelectionDialog/Visibility", 0.0 );
            this.Drawing.Update();	 
         }
         else if( origin == "TopMap" )
         {
            // Close top map window
            this.Drawing.SetDResource( "TopMap/Visibility", 0.0 );
            this.Drawing.Update();	 
         }
      }
      return;
   }
   
   if( format == "Button" )  // Handle button clicks
   {	 
      if( action != "Activate" )
        return;
      
      this.PanMode = false;    // Abort Pan mode
      
      // Abort ZoomTo/Drag modes (if any) on both maps.
      this.Map[ 0 ].SetZoom( null, 'e', 0.0 );
      this.Map[ 1 ].SetZoom( null, 'e', 0.0 );
      
      if( origin == "CloseDialog" )
      {
         this.Drawing.SetDResource( "SelectionDialog/Visibility", 0.0 );
         this.Drawing.Update();	 
      }
      else if( origin == "ToggleLock" )
      {
         this.SetLocking( !this.LockSelectedPlane );
         this.Drawing.Update();	 
      }
      else if( origin == "Up" )
      {
         this.Zoom( 'u', 0.0 );
         /* Rotate the thumbnail globe to show the same area as the 
            detailed map.
         */
         this.SyncGlobeWithDetailedMap( "Map" );
         this.Drawing.Update();
      }
      else if( origin == "Down" )
      {	    
         this.Zoom( 'd', 0.0 );
         this.SyncGlobeWithDetailedMap( "Map" );
         this.Drawing.Update();
      }
      else if( origin == "Left" )
      {	    
         this.Zoom( 'l', 0.0 );
         this.SyncGlobeWithDetailedMap( "Map" );
         this.Drawing.Update();
      }
      else if( origin == "Right" )
      {	    
         this.Zoom( 'r', 0.0 );
         this.SyncGlobeWithDetailedMap( "Map" );
         this.Drawing.Update();
      }
      else if( origin == "ZoomIn" )
      {
         this.Zoom( 'i', 2.0 );
         this.HandleZoomLevel();
         this.Drawing.Update();	 
      }
      else if( origin == "ZoomOut" )
      {
         this.Zoom( 'o', 2.0 );
         this.HandleZoomLevel();
         this.Drawing.Update();	 
      }
      else if( origin == "ZoomReset" )
      {	
         this.Zoom( 'n', 0.0 );
         this.HandleZoomLevel();
         this.Drawing.Update();	 
      }
      else if( origin == "ZoomTo" )
      {
         // Abort a possible Drag mode on thumbnail map.
         this.Map[ 0 ].SetZoom( null, 'e', 0.0 );
         
         // Start ZoomTo mode on the detailed map.
         this.Map[ 1 ].SetZoom( null, 't', 0.0 );
         this.SetStatus( "Define a rectangular area on the detailed map to zoom to." );
         this.SuspendPromptUpdates = true;
         this.Drawing.Update();	 
      }
      else if( origin == "Pan" )
      {	    
         this.PanMode = true;
         this.SetStatus( "Click to define a new center." );
         this.SuspendPromptUpdates = true;
         this.Drawing.Update();	 
      }
      else if( origin == "Drag" )
      {
         /* Activate dragging mode on both maps. Dragging will start on the
            mouse click. If no object of interest is selected by the mouse 
            click, the dragging will be started by the code in the Trace 
            callback anyway. The "Drag" button demonstrates an alternative 
            way to start dragging from a button.
         */
         this.DraggingFromButton = true;
         this.Map[ 0 ].SetZoom( null, 's', 0.0 );
         this.Map[ 1 ].SetZoom( null, 's', 0.0 );
         this.SetStatus( "Click and drag the map with the mouse." );
         this.SuspendPromptUpdates = true;
         this.Drawing.Update();
      }
      else if( origin == "AirportLabels" )
      {
         this.CityLabels = !this.CityLabels;
         this.SetGISLayers( 1 );
         this.Drawing.Update();	 
      }
      else if( origin == "Planes" )
      {
         ToggleResource( this.Map[ 1 ], "PlaneGroup/Visibility" );
         this.Drawing.Update();	 
      }
      else if( origin == "ValueDisplay" )
      {
         // Visibility of all labels is constrained, set just one.
         ToggleResource( this.PlaneArray[ 0 ].graphics[ 1 ],
                         "Label/Visibility" );
         this.Drawing.Update();
      }
      else if( origin == "ToggleStates" )
      {
         this.StateDisplay = !this.StateDisplay;
         this.SetGISLayers( 0 );    // Thumbnail map
         this.SetGISLayers( 1 );    // Detailed map 
         
         this.Drawing.Update();	 
      }
      else if( origin == "Update" )
      {
         this.PerformUpdates = !this.PerformUpdates;
         this.Drawing.Update();
      }	
      else if( origin == "PlaneSize" )
      {
         // Change plane icon's size.
         if( this.PlaneSize == SMALL_SIZE )
           this.PlaneSize = MEDIUM_SIZE;
         else if( this.PlaneSize == MEDIUM_SIZE )
           this.PlaneSize = BIG_SIZE;
         else // BIG_SIZE
           this.PlaneSize = SMALL_SIZE;	 
         
         this.SetPlaneSize();
         this.Drawing.Update();	 
      }	
   }
   /* Process mouse clicks on plane icons, implemented as an Action with
      the Plane label attached to an icon and activated on a mouse click. 
   */
   else if( format == "CustomEvent" )
   {
      /* Ignore selection in the DraggingFromButton mode,
         as well as in the Scroll mode on mobile devices.
      */
      if( this.DraggingFromButton || ( this.ScrollMode && this.IsMobile ) )
        return;

      let event_label = message_obj.GetSResource( "EventLabel" );   /* String */
      
      if( event_label == "Plane" )
      {
         /* Map dragging mode is activated on a mouse click in the trace 
            callback. Abort the dragging mode if an object with custom event
            was selected. This gives custom events a higher priority compared 
            to the dragging mode. If it's a ZoomTo mode activated by a button,
            don't abort and ignore the object selection.
         */
         let zoom_mode = this.ZoomToMode();   /* int */
         if( zoom_mode == 0 ||
             ( zoom_mode & GLG.GlgZoomState.PAN_DRAG_STATE ) != 0 )
         {
            if( zoom_mode != 0 )
              this.Map[1].SetZoom( null, 'e', 0.0 );  /* Abort zoom mode */
            
            let data_index =  /* int */
              Math.trunc( message_obj.GetDResource( "Object/DataIndex" ) );
            
            if( this.SelectedPlane != this.PlaneArray[ data_index ] )
            {
               SelectPlane( this.SelectedPlane, 0 );  // Unhighlight old
               this.SelectedPlane = this.PlaneArray[ data_index ];
               SelectPlane( this.SelectedPlane, 1 );       // Highlight new 
               this.Drawing.Update();
            }
         }
      }
   }
   else if( action == "Zoom" )
   {
      // Disable locking: we may be zooming on a different area.
      this.SetLocking( false );
      
      if( subaction == "Start" )   // Starting ZoomTo
      {
         this.SuspendPromptUpdates = true;
      }
      else if( subaction == "ZoomRectangle" )
      {
         /* ZoomTo rectangle created - set a custom distinct color
            good for colors at all zoom levels.
         */
         let zoom_rect =   /* GlgObject */
           this.Map[1].GetResourceObject( "GlgZoomRect" );
         zoom_rect.SetGResource( "EdgeColor", 1.0, 0.0, 0.0 );
      }
      else if( subaction == "End" )   // Finishing ZoomTo
      {
         /* Rotate the thumbnail globe to show the same area as the 
            detailed map.
         */
         this.SyncGlobeWithDetailedMap( origin );
            
         this.HandleZoomLevel();
         this.SuspendPromptUpdates = false;
         this.Drawing.Update();
      }
      // Aborting ZoomTo (right mouse button, etc.).
      else if( subaction == "Abort" )
      {
         this.SuspendPromptUpdates = false;
      }
   }
   else if( action == "Pan" )
   {
      // Disable locking when scrolling the map with the mouse.
      this.SetLocking( false );
      
      // Starting dragging with the mouse
      if( subaction == "Start" )    // Map dragging start
      {
         this.SetStatus( "Drag the map with the mouse." );
         this.SuspendPromptUpdates = true;
      }
      else if( subaction == "Drag" ||         // Dragging
               subaction == "ValueChanged" )  // Scrollbars
      {
         if( subaction == "Drag" )
           this.DraggingFromButton = false;

         /* When dragging the detailed map with the mouse, rotate the 
            thumbnail globe to show the same area as the detailed map.
         */
         this.SyncGlobeWithDetailedMap( origin );
         this.Drawing.Update();
      }
      else if( subaction == "End" )      // Map dragging end
      {
         this.DraggingFromButton = false;
         this.SuspendPromptUpdates = false;
         this.SetStatus( "" );   // Reset prompt when done dragging.
         
         /* Reset dragging mode on both maps, in case it was started 
            with the Drag button.
         */
         this.Map[ 0 ].SetZoom( null, 'e', 0.0 );
         this.Map[ 1 ].SetZoom( null, 'e', 0.0 );
         this.Drawing.Update();         
      }
      // Dragging aborted (right mouse button, etc.).
      else if( subaction == "Abort" )
      {
         this.DraggingFromButton = false;
         this.SuspendPromptUpdates = false;
         this.SetStatus( "" );   // Reset prompt when aborting dragging.
         this.Drawing.Update();         
      }
   }
}

//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.ZoomToMode = function()   /* int */
{
   // Check if the detailed map is in the ZoomTo mode.
   return Math.trunc( this.Map[ 1 ].GetDResource( "ZoomToMode" ) );
}

//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.Zoom = function( /* String */ type, /* double */ value )
{
   switch( type )
   {
    default:
      this.Map[ 1 ].SetZoom( null, type, value );
      this.CheckScrollLimits( type );
      this.UpdateMap( 1 );

      /* Sync thumbnail map when panning. After "1:1" zoom reset, the maps' 
         centers may differ, sync the thumbnail map when zooming in the 
         first time.
      */
      switch( type )
      {
       case 'i':
         this.SyncGlobeWithDetailedMap( null );
         break;
       case 'u':
       case 'd':
       case 'l':
       case 'r':
         this.SyncGlobeWithDetailedMap( null );
         break;
       default: break;
      }
      break;
      
    case 'n':
      if( this.MapProjection[ 0 ] == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
      {
         // Reset thumbnail globe to initial position.
         this.GISObject[ 0 ].SetGResourceFromPoint( "GISCenter",
                                                    this.InitCenter[0] );
         this.GISObject[ 0 ].SetGResourceFromPoint( "GISExtent",
                                                    this.InitExtent[0] );
         
         this.UpdateMap( 0 );
      }
      
      // Reset detailed map to initial extent.
      this.GISObject[ 1 ].SetGResourceFromPoint( "GISCenter",
                                                 this.InitCenter[1] );
      this.GISObject[ 1 ].SetGResourceFromPoint( "GISExtent",
                                                 this.InitExtent[1] );
      this.UpdateMap( 1 );
      
      // Make selected area rectangle invisible when no zoom
      this.GISObject[ 0 ].SetDResource( "GISArray/SelectedArea/Visibility",
                                        0.0 );
      break;
   }
}

//////////////////////////////////////////////////////////////////////////
// For rectangular projection on the detailed map, make sure the map 
// does not scroll beyond the poles in the vertical direction.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.CheckScrollLimits = function( /* String */ type )
{
   let extent, center;      /* GlgPoint */
   let min_y, max_y;        /* double  */
   let adjust_x, adjust_y;  /* boolean */
      
   if( this.MapProjection[ 1 ] == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
     return;   // Allow unlimited scrolling on ortho.
   
   switch( type )
   {
    case 'u':  // Scroll up
    case 'd':  // Scroll down
    case 'l':  // Scroll left
    case 'r':  // Scroll right
      break; // Adjust only for scroll types.
    default: return;   // Don't adjust for other zoom types.
   }
   
   extent = this.GISObject[ 1 ].GetGResource( "GISExtent" );
   center = this.GISObject[ 1 ].GetGResource( "GISCenter" );
   
   min_y = center.y - extent.y / 2.0;
   max_y = center.y + extent.y / 2.0;
   
   /* Check and adjust center lat to make sure the map does not scroll 
      beyond the poles in the vertical direction. 
   */
   adjust_y = true;
   if( extent.y >= 180.0 )
     center.y = 0.0;
   else if( min_y < -90.0 )
     center.y = -90.0 + extent.y / 2.0;
   else if( max_y > 90.0 )
     center.y = 90.0 - extent.y / 2.0;
   else
     adjust_y = false;
   
   /* Allow scrolling tp +-180 in horizontal direction, to match the
      range of the horizontal scrollbar.
   */
   adjust_x = true;
   if( center.x < -180.0 )
     center.x = -180.0;
   else if( center.x > 180.0 )
     center.x = 180.0;
   else
     adjust_x = false;
   
   // Set adjusted center
   if( adjust_x || adjust_y )
     this.GISObject[ 1 ].SetGResourceFromPoint( "GISCenter", center );
}

//////////////////////////////////////////////////////////////////////////
// Rotates the globe on the thumbnail map to show the same place as the
// detailed map.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SyncGlobeWithDetailedMap = function( /* String */ origin )
{
   let center, globe_center;   /* GlgPoint */
      
   // Update the detailed map, unless it's thumbnail map dragging.
   if( origin != "TopMap" )
     this.UpdateMap( 1 );
   
   /* Sync up only if the detailed map (origin == "Map") is rotated, 
      not the thumbnail map (origin == "TopMap").
   */
   if( origin != null && origin == "Map" )
   {
      // Get the center of the detailed map.
      center = this.GISObject[ 1 ].GetGResource( "GISCenter" );
      
      // Get the center of the thumbnail globe.
      globe_center = this.GISObject[ 0 ].GetGResource( "GISCenter" );
      
      // Sync up if centers differ.
      if( globe_center.x != center.x || globe_center.y != center.y ||
          globe_center.z != center.z )
      {
         // Rotate the thumbnail globe to show the same area.
         this.GISObject[ 0 ].SetGResourceFromPoint( "GISCenter", center );
         
         this.UpdateMap( 0 );
      }
   }
   else
     /* Don't sync when the globe on the thumbnail map is rotated
        (origin == "TopMap"), to allow moving it separately. Just
        update the selected area display.
     */
     this.UpdateMap( 0 );
}

//////////////////////////////////////////////////////////////////////////
// Used to obtain coordinates of the mouse click. 
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.TraceCallback =
  function( /* GlgObject */ viewport, /* GlgTraceData */ trace_info )
{      
   if( !this.Active )
     return;

   let map;   /* int */   
   let event_type = trace_info.event_type;

   // Use the Map area events only.
   if( trace_info.viewport.Equals( this.Map[ 0 ] ) )
     map = 0;
   else if( trace_info.viewport.Equals( this.Map[ 1 ] ) )
     map = 1;
   else
   {
      /* Erase the current postion display when the mouse moves outside 
         of the map.
      */
      switch( event_type )
      {
       case GLG.GlgEventType.MOUSE_MOVED:
         this.PositionObject.SetSResource( null, "" );
         this.PositionArea.Update();
         break;
       default: break;
      }
      return;
   }

   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_START:
      if( !this.DraggingFromButton && !this.ScrollMode )
        return;
 
      GLG.SetTouchMode();        /* Start dragging via touch events. */
      /* Fall through */

    case GLG.GlgEventType.TOUCH_MOVED:
      if( !GLG.GetTouchMode() )
        return;
      /* Fall through */
    case GLG.GlgEventType.MOUSE_PRESSED:
    case GLG.GlgEventType.MOUSE_MOVED:
     this.point.x = trace_info.mouse_x * this.CoordScale;
     this.point.y = trace_info.mouse_y * this.CoordScale;
     this.point.z = 0.0;
     
      /* COORD_MAPPING_ADJ is added to the cursor coordinates for precise
         pixel mapping.
      */
      this.point.x += GLG.COORD_MAPPING_ADJ;
      this.point.y += GLG.COORD_MAPPING_ADJ;
      break;
        
    default: return;
   }

   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_MOVED:
    case GLG.GlgEventType.MOUSE_MOVED: 
      // Report lat/lon position under the mouse.

      /* Converts X/Y to lat/lon using GIS object's current projection,
         handles both maps.
      */
      this.GISObject[ map ].GISConvert( null, GLG.GlgCoordType.SCREEN_COORD,
                                        /* X/Y to Lat/Lon */ true,
                                        this.point, this.lat_lon );
      
      this.PositionObject.SetSResource( null,
                                        CreateLocationString( this.lat_lon ) );
      this.PositionArea.Update();
      break;
      
    case GLG.GlgEventType.TOUCH_START:
    case GLG.GlgEventType.MOUSE_PRESSED:
      if( this.ZoomToMode() != 0 )
        return; // ZoomTo or dragging mode in progress: pass it through.
      
      // Handle paning: set the new map center to the location of the click.
      if( this.PanMode )
      {
         if( trace_info.button != 1 )
           return;  // Use only the left button clicks.
         
         this.PanMode = false;
         
         /* Converts X/Y to lat/lon using GIS object's current projection,
            handles clicks on either map.
         */
         this.GISObject[ map ].GISConvert( null, GLG.GlgCoordType.SCREEN_COORD,
                                           /* X/Y to Lat/Lon */ true,
                                           this.point, this.lat_lon );
         
         if( this.MapProjection[ 0 ] == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
         {
            /* For the orthographic projection, Pan/Rotate the globe on the 
               thumbnail map as well. Don't do anything for the rectangular 
               projection: the whole world is already displayed.
            */
            this.GISObject[ 0 ].SetGResourceFromPoint( "GISCenter",
                                                       this.lat_lon );
            this.UpdateMap( 0 );
            this.Map[ 0 ].Update();
         }
         
         // Pan the detailed map.
         this.GISObject[ 1 ].SetGResourceFromPoint( "GISCenter", this.lat_lon );
         this.UpdateMap( 1 );
         
         // Disable locking: we scrolled the map to see a different area.
         this.SetLocking( false );
         this.Drawing.Update();
      }
      else
        /* Not a pan mode: start dragging the map with the mouse. */
        this.Map[ map ].SetZoom( null, 's', 0.0 );
      break;
      
    default: return;
   }      
}

//////////////////////////////////////////////////////////////////////////
// Adjust selected region on the thumbnail map to match detailed map.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetSelectedArea = function()
{
   // Set the coordinates of the SelectedArea polygon.
   let rect =   /* GlgObject  */
     this.GISObject[ 0 ].GetResourceObject( "GISArray/SelectedArea" );
      
   let extent = this.GetExtentDegrees( 1 );   /* GlgPoint */

   if( extent.x >= 120.0 )
   {
      // Big area: don't need to show.
      rect.SetDResource( "Visibility", 0.0 );
   }
   else
   {
      rect.SetDResource( "Visibility", 1.0 );
      
      /*  Get lat/lon of the visible area of the detailed map. 
          Use 16 points for better precision, since the area is not 
          rectangular in the orthographic projection used for the thumbnail 
          globe.
      */
      let lat_lon = new Array( 16 );   /* GlgPoint[] */
      lat_lon[ 0  ] = this.GetLatLon( -1000.0, -1000.0, 1 );
      lat_lon[ 1  ] = this.GetLatLon( -1000.0,  -500.0, 1 );
      lat_lon[ 2  ] = this.GetLatLon( -1000.0,     0.0, 1 );
      lat_lon[ 3  ] = this.GetLatLon( -1000.0,   500.0, 1 );
      lat_lon[ 4  ] = this.GetLatLon( -1000.0,  1000.0, 1 );
      lat_lon[ 5  ] = this.GetLatLon(  -500.0,  1000.0, 1 );
      lat_lon[ 6  ] = this.GetLatLon(     0.0,  1000.0, 1 );
      lat_lon[ 7  ] = this.GetLatLon(   500.0,  1000.0, 1 );
      lat_lon[ 8  ] = this.GetLatLon(  1000.0,  1000.0, 1 );
      lat_lon[ 9  ] = this.GetLatLon(  1000.0,   500.0, 1 );
      lat_lon[ 10 ] = this.GetLatLon(  1000.0,     0.0, 1 );
      lat_lon[ 11 ] = this.GetLatLon(  1000.0,  -500.0, 1 );
      lat_lon[ 12 ] = this.GetLatLon(  1000.0, -1000.0, 1 );
      lat_lon[ 13 ] = this.GetLatLon(   500.0, -1000.0, 1 );
      lat_lon[ 14 ] = this.GetLatLon(     0.0, -1000.0, 1 );
      lat_lon[ 15 ] = this.GetLatLon(  -500.0, -1000.0, 1 );
      
      for( let i=0; i<16; ++i )
      {
         // Get polygon's points
         let point_obj = rect.GetElement( i );   /* GlgObject */
         
         // Set point's lat/lon
         point_obj.SetGResourceFromPoint( null, lat_lon[ i ] );
      }
   }
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Starts simulation for a plane, 
//     assigns its start and end nodes.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.StartPlane =
  function( /* PlaneData */ plane, /* boolean */ init )
{
   if( this.NumNodes < 2 )
   {
      AppAlert("Less then two nodes: can't start planes.");
      return;
   }
   
   let to_index, from_index;   /* int */
   from_index = Math.trunc( GLG.Rand( 0, this.NumNodes - 0.001 ) );
   do
   {
      to_index = Math.trunc( GLG.Rand( 0, this.NumNodes - 0.001 ) );
   } while( to_index == from_index );

   plane.from_node = this.NodeArray[ from_index ];
   plane.to_node = this.NodeArray[ to_index ];
   plane.flight_number = Math.trunc( GLG.Rand( 101.0, 1999.0 ) );
   plane.speed = GLG.Rand( 0.4, 1.0 );   // Vary plane speed

   if( init )
   {
      plane.path_position = GLG.Rand( 0.1, 0.2 );
      plane.path_position_last = plane.path_position - 0.05;  // For angle
   }
   else
   {
      plane.path_position = 0.0;
      plane.path_position_last = 0.0;
   }
   
   plane.tooltip[ 0 ] = "Flight " + plane.flight_number;
   
   // On the detailed map, add from/to node info to the tooltip.
   plane.tooltip[ 1 ] = plane.tooltip[ 0 ] +
     " from " + plane.from_node.name + " to " + plane.to_node.name;
   
   // Set the tooltip
   for( let i = 0; i < 2; ++i )      
     plane.graphics[ i ].SetSResource( "TooltipString", plane.tooltip[ i ] );

   // On detailed map[1], show the flight number as icon label
   plane.graphics[1].SetSResource( "LabelString",
                                   "Flight " + plane.flight_number );

   // Stop tracking the selected flight when it reaches destination.
   if( plane == this.SelectedPlane )
     this.SetLocking( false );
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Calculates plane's lat/lon using simulated 
//     data. In an application, it will query the current plane position.
//
// The simulation moves the plane from the start to the end node/city
// as controlled by the path_position parameter. The path_position changes
// in the range from from 0 (start node) to 1 (end node).
//////////////////////////////////////////////////////////////////////////
function GetPlaneLatLon( /* PlaneData */ plane )
{
   plane.lat_lon.x = RELATIVE_TO_NEW_RANGE( plane.from_node.lat_lon.x, 
                                            plane.to_node.lat_lon.x,
                                            plane.path_position );
   
   plane.lat_lon.y = RELATIVE_TO_NEW_RANGE( plane.from_node.lat_lon.y, 
                                            plane.to_node.lat_lon.y,
                                            plane.path_position );
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Select some plane on the initial appearance.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.InitSelection = function()
{
   // Select the first plane
   this.SelectedPlane = this.PlaneArray[ 0 ];
   SelectPlane( this.SelectedPlane, 1 );
   
   // Lock on the selected plane, pan the map to keep it visible.
   this.SetLocking( true );

   // Rotate thumbnail globe too to show the same location.
   if( this.MapProjection[ 0 ] == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
     this.GISObject[ 0 ].SetGResource( "GISCenter", -95.35, 37.37, 0.0 );
}

//////////////////////////////////////////////////////////////////////////
// UTILITY FUNCTION: Calculates an angle between the line defined by two 
// points and the X axis.
//////////////////////////////////////////////////////////////////////////
function GetAngle( /* GlgPoint */ pt1, /* GlgPoint */ pt2 )   /* double */
{
   let length, angle;   /* double */
      
   length = GetLength( pt1, pt2 );
   
   if( length == 0.0 )
     angle = 0.0;
   else
   {
      angle = Math.acos( ( pt2.x - pt1.x ) / length );
      
      if( pt2.y - pt1.y < 0.0 )  // ScreenSpace Z axis points to the user.
        angle = - angle;
   }
   
   return RadToDeg( angle );
}

//////////////////////////////////////////////////////////////////////////
// UTILITY FUNCTION: Calculates a distance between two points in 2D.
//////////////////////////////////////////////////////////////////////////
function GetLength( /* GlgPoint */ pt1, /* GlgPoint */ pt2 )   /* double */
{
   return Math.sqrt( ( pt2.x - pt1.x ) * ( pt2.x - pt1.x ) +
                     ( pt2.y - pt1.y ) * ( pt2.y - pt1.y ) );
}

//////////////////////////////////////////////////////////////////////////
// Turns plane icons' labels ON or OFF on the detailed map.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetPlaneLabels = function( /* boolean */ on )
{
   let label =    /* GlgObject */
     this.PlaneArray[ 0 ].graphics[ 1 ].GetResourceObject( "Label" );
   if( label != null )
     label.SetDResource( "Visibility", on ? 1.0 : 0.0 );
}

//////////////////////////////////////////////////////////////////////////
// Sets locking mode ON or OFF. If locking is ON, the map is automatically
// scrolled to keep the selected plane icon in view.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetLocking = function( /* boolean */ lock_state )
{
   this.LockSelectedPlane = lock_state;
}

//////////////////////////////////////////////////////////////////////////
// Displays the selected plane's location and locking status.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.UpdateSelectedPlaneStatus = function()
{
   let message;   /* String */

   if( this.SuspendPromptUpdates )
     return;
   
   if( this.SelectedPlane != null )
   {
      GetPlaneLatLon( this.SelectedPlane );
      
      if( this.LockSelectedPlane )
        message = "Map is locked on selected Flight " + 
          this.SelectedPlane.flight_number + ", " + 
          CreateLocationString( this.SelectedPlane.lat_lon );
      else
        message = "Selected Flight " + this.SelectedPlane.flight_number +
          ", " + CreateLocationString( this.SelectedPlane.lat_lon );
   }
   else
     message = "Click on the plane icon to select.";
   this.Drawing.SetSResource( "StatusLabel/String", message );
}

//////////////////////////////////////////////////////////////////////////
// Displays a message in the status area.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetStatus = function( /* String */ message )
{
   this.Drawing.SetSResource( "StatusLabel/String", message );
   this.Drawing.GetResourceObject( "StatusArea" ).Update();
}

//////////////////////////////////////////////////////////////////////////
// Centers the map on the selected plane, used when locking mode is ON.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.CenterOnPlane =
  function( /* PlaneData */ plane, /* int */ map )
{      
   // Center the map on the plane
   this.GISObject[ map ].SetGResourceFromPoint( "GISCenter", plane.lat_lon );
}

//////////////////////////////////////////////////////////////////////////
// Highlights the selected plane on both maps by changing its 
// SelectedIndex value.
//////////////////////////////////////////////////////////////////////////
function SelectPlane( /* PlaneData */ plane, /* int */ selected )
{
   for( let i=0; i < 2; ++i )
     if( plane.graphics[ i ] != null )
       plane.graphics[ i ].SetDResource( "SelectedIndex", selected );
}

//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetPlaneSize = function()
{
   for( let i=0; i<2; ++i )
   {
      let resource =   /* GlgObject */
        this.PlaneTemplate[ i ].GetResourceObject( "IconScale" );
      if( resource != null )
        resource.SetDResource( null, this.PlaneSize );
   }
}

//////////////////////////////////////////////////////////////////////////
// Toggle resource between 0 and 1.
//////////////////////////////////////////////////////////////////////////
function ToggleResource( /* GlgObject */ glg_object, /* String */ res_name )
{
   let value = glg_object.GetDResource( res_name );  /* double */
   glg_object.SetDResource( res_name, value != 0.0 ? 0.0 : 1.0 );
}

const CITY_ON_1    = ",cities_100K,cities_500K,cities_1M,cities_3M,cities_6M";
const CITY_OFF_1   = ",-cities_100K,-cities_500K,-cities_1M,-cities_3M,-cities_6M";
const STATES_ON_1  = ",states";
const STATES_OFF_1 = ",-bnd_polbndl_intern,-bnd_polbndl_prim,-states";

const CITY_ON_2    = ",us_cities";
const CITY_OFF_2   = ",-us_cities";
const STATES_ON_2  = ",states";
const STATES_OFF_2 = ",-states";

////////////////////////////////////////////////////////////////////////
// Toggle map layers: airport/city labels and states.
////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetGISLayers = function( /* int */ map )
{
   let layers;   /* String */

   let CITY_ON, CITY_OFF, STATES_ON, STATES_OFF;    /* STring */
   if( !this.SuppliedMapServerURL )
   {
      CITY_ON = CITY_ON_1;
      CITY_OFF = CITY_OFF_1;
      STATES_ON = STATES_ON_1;
      STATES_OFF = STATES_OFF_1;
   }
   else
   {
      CITY_ON = CITY_ON_2;
      CITY_OFF = CITY_OFF_2;
      STATES_ON = STATES_ON_2;
      STATES_OFF = STATES_OFF_2;
   }
   
   // Airport labels should be visible only when city labels are off.
   this.NodeTemplate[1].SetDResource( "Label/Visibility",
                                      this.CityLabels ? 0.0 : 1.0 );
   
   layers = "default_gis";
   
   // Add city layers if they are on on the detailed map.
   if( map == 1 )
     if( this.CityLabels )
       layers = layers + CITY_ON;
     else
       layers = layers + CITY_OFF;
   
   if( this.StateDisplay )   // Add states layer if it is on.
     // Enable states regardless of the default.
     layers = layers + STATES_ON;
   else
     // Disable state outline display.
     layers = layers + STATES_OFF;
   
   this.GISObject[ map ].SetSResource( "GISLayers", layers );
}

////////////////////////////////////////////////////////////////////////
// Convenience wrapper
////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.GetLatLon =
  function( /* double */ x, /* double */ y, /* int */ map )   /* GlgPoint */
{
   let lat_lon = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */

   this.util_point.x = x;
   this.util_point.y = y;
   this.util_point.z = 0.0;
   this.GISObject[ map ].GISConvert( null, GLG.GlgCoordType.OBJECT_COORD,
                                     /* X/Y to Lat/Lon */ true,
                                     this.util_point, lat_lon );
   return lat_lon;
}

//////////////////////////////////////////////////////////////////////////
function RELATIVE_TO_NEW_RANGE( /* double */ low, /* double */ high,
                                /* double */ rel_value )   /* double */
{
   return ( low + ( high - low ) * rel_value );
}

//////////////////////////////////////////////////////////////////////////
function RadToDeg( /* double */ angle )   /* double */
{
   return angle / Math.PI * 180.0;
}

//////////////////////////////////////////////////////////////////////////
// Turn airport and plane labels on or off depending on the zoom level and
// adjust plane icon size. 
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.HandleZoomLevel = function()
{
   let extent;     /* GlgPoint */
   let high_zoom;  /* boolean */
      
   extent = this.GetExtentDegrees( 1 );
   
   high_zoom = ( extent.x < 100.0 && extent.y < 50.0 );
   
   this.SetPlaneLabels( high_zoom );     // Plane labels.
   
   this.CityLabels = true;
   this.SetGISLayers( 1 );     // Airport labels.
   
   // Plane icons size.
   this.PlaneSize = ( high_zoom ? MEDIUM_SIZE : SMALL_SIZE );
   this.SetPlaneSize();
}

//////////////////////////////////////////////////////////////////////////
// Gets extent in lat/lon.
// For the ortho projection, roughly converts from meters to lat/lon.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.GetExtentDegrees = function( /* int */ map )   /* GlgPoint */
{   
   let extent = this.GISObject[ map ].GetGResource( "GISExtent" ); /*GlgPoint*/
   if( this.MapProjection[ map ] == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
   {
      extent.x = extent.x / GLG.EQUATOR_RADIUS * 90.0;
      extent.y = extent.y / GLG.POLAR_RADIUS * 90.0;
   }
   return extent;
}

//////////////////////////////////////////////////////////////////////////
// Generate a location info string by converting +- sign info into the
// N/S, E/W suffixes, and decimal fraction to deg, min, sec.
//////////////////////////////////////////////////////////////////////////
function CreateLocationString( /* GlgPoint */ point )   /* String */
{
   let  /* int */
     x_deg, y_deg,
     x_min, y_min,
     x_sec, y_sec;
   let  /* String */
        char_x,
        char_y;
   let lat, lon;  /* double */

   if( point.z < 0.0 )
     return "";

   lon = point.x;
   lat = point.y;
   
   if( lon < 0.0 )
   {
      lon = -lon;
      char_x = 'W';
   }
   else if( lon >= 360.0 )
   {
      lon -= 360.0;
      char_x = 'E';
   }
   else if( lon >= 180.0 )
   {
      lon = 180.0 - ( lon - 180.0 );
      char_x = 'W';
   }
   else
     char_x = 'E';
   
   if( lat < 0.0 )
   {
      lat = -lat;
      char_y = 'S';
   }
   else
     char_y = 'N';
   
   x_deg = Math.trunc( lon );
   x_min = Math.trunc( ( lon - x_deg ) * 60.0 );
   x_sec = Math.trunc( ( lon - x_deg - x_min / 60.0 ) * 3600.0 );
   
   y_deg = Math.trunc( lat );
   y_min = Math.trunc( ( lat - y_deg ) * 60.0 );
   y_sec = Math.trunc( ( lat - y_deg - y_min / 60.0 ) * 3600.0 );
      
   return "Lon=" + x_deg + "\u00B0" +
     padded( x_min ) + "'" + padded( x_sec ) + "\"" + char_x +
     "  Lat=" + y_deg + "\u00B0" + 
     padded( y_min ) + "'" + padded( y_sec ) + "\"" + char_y;
}

//////////////////////////////////////////////////////////////////////////
// Pads the value with 0 if needed to have a constant field width. 
//////////////////////////////////////////////////////////////////////////
function padded( /* int */ value )   /* String */
{
   if( value < 10 )
     return "0" + value;
   else
     return "" + value;
}

//////////////////////////////////////////////////////////////////////////
// Zoom to the US area after a few seconds to show details.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.ZoomToUSStart = function()
{
   if( !this.Active )
     return;
   
   // Show zoom message.
   this.Drawing.SetDResource( "Map/USZoomingMessage/Visibility", 1.0 );
   this.Drawing.Update();
   
   // Start zooming on a timer to let the browser show the message.
   setTimeout( ()=>this.ZoomToUS(), 50 );
}

//////////////////////////////////////////////////////////////////////////
// Perform zoom.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.ZoomToUS = function()
{
   if( !this.Active )
     return;
   
   /* GISExtent is in lon/lat for the rectangular GIS projection,
      and in meters for the orthographic projection.
      To find proper values, zoom the map in the GlgBuilder and 
      copy the GISExtent values. */
   
   // Zoom to the US boundaries on detailed map.
   this.GISObject[ 1 ].SetGResource( "GISCenter", -95.35, 37.37, 0.0 );
   this.GISObject[ 1 ].SetGResource( "GISExtent", 69.71, 34.85, 0.0 );
   
   if( this.MapProjection[ 0 ] == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
     // Rotate thumbnail globe too to show the same location.
     this.GISObject[ 0 ].SetGResource( "GISCenter", -95.35, 37.37, 0.0 );
   
   this.HandleZoomLevel();
   
   this.UpdateMap( 1 );
   this.UpdateMap( 0 );
   
   /* Reorder US zoom message to top, otherwise airplane icons 
      would be flying on top of it.
   */
   let us_message =   /* GlgObject */
     this.Drawing.GetResourceObject( "Map/USZoomingMessage" );
   this.Map[1].ReorderElement( this.Map[1].GetIndex( us_message ),
                               this.Map[1].GetSize() - 1 );

   this.Drawing.Update();

   // Erase zoom message after a delay.
   setTimeout( ()=>this.ZoomToUSEnd(), US_ZOOM_DELAY2 );
}

//////////////////////////////////////////////////////////////////////////
// Remove the US zooming message after a few seconds.
//////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.ZoomToUSEnd = function()
{
   if( !this.Active )
     return;

   this.Drawing.SetDResource( "Map/USZoomingMessage/Visibility", 0.0 );
   this.Drawing.Update();
}

//////////////////////////////////////////////////////////////////////////////
// Loads any assets required by the application and invokes the specified
// callback when done.
// Alternatively, the application drawing can be loaded as an asset here
// as well, so that it starts loading without waiting for the other assets 
// to finish loading.
//////////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.LoadAssets = function( callback, user_data )
{
   Debug( "LoadAssets for: " + this.GLG_div_name );
   
   if( !this.Active )
     return;

   /* Define an internal variable to keep the number of loaded assets. */
   this.NumLoadedAssets = 0;

   /* HTML5 doesn't provide a scrollbar input element (only a range input 
      html element is available). This application needs to load GLG scrollbars
      used for integrated chart scrolling. For each loaded scrollbar, the 
      AssetLoaded callback is invoked with the supplied data array parameter.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_h.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_h", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_v.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_v", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );   
}

//////////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.AssetLoaded = function( glg_object, data, path )
{
   if( !this.Active )
     return;
   
   if( data.name == "scrollbar_h" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgHScrollbar", glg_object );
   }
   else if( data.name == "scrollbar_v" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgVScrollbar", glg_object );
   }
   else
     AppError( "Unexpected asset name" );

   ++this.NumLoadedAssets;
    
   // Invoke the callback after all assets have been loaded.
   if( this.NumLoadedAssets == 2 )
     data.callback( data.user_data );
}

//////////////////////////////////////////////////////////////////////////////
// Changes drawing size while maintaining width/height aspect ratio.
//////////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetDrawingSize = function( next_size )
{
   const ASPECT_RATIO = 800 / 650;
   
   const MIN_WIDTH = 600;
   const MAX_WIDTH = 1000;
   const SCROLLBAR_WIDTH = 15;

   let span = document.body.clientWidth - SCROLLBAR_WIDTH;    

   if( this.SizeIndex == undefined )   // First time: initialize.
   {
      this.SizeIndex = 0;

      if( this.IsMobile )
      {
         /* Mobile devices use fixed device-width: disable Change Drawing Size 
            button. If it's not standalone, it is handled outside of this 
            script.
         */
         if( this.Standalone )
           RemoveElement( null, this.GLG_div_name + "_change_size" );
      }
      else   /* Desktop */
      {      
         const small_sizes  = [ 1, 1.5,  2.,   2.5 ];
         const medium_sizes = [ 1, 0.75, 1.25, 1.5 ];
         const large_sizes  = [ 1, 0.6,  1.25, 1.5 ];
         
         if( span < 600 )
           this.SetDrawingSize.size_array = small_sizes;
         else if( span < 800 )
           this.SetDrawingSize.size_array = medium_sizes;
         else
           this.SetDrawingSize.size_array = large_sizes;
         
         this.SetDrawingSize.num_sizes = this.SetDrawingSize.size_array.length;
         
         /* Handle browser zooming. */
         this.ResizeListener = ()=>this.SetDrawingSize( false );
         window.addEventListener( "resize", this.ResizeListener );
      }
   }
   else if( next_size )
   {
      ++this.SizeIndex;
      this.SizeIndex %= this.SetDrawingSize.num_sizes;
   }
    
   let drawing_area = document.getElementById( this.GLG_div_name );
   if( this.IsMobile )
   {
      /* Mobile devices use constant device-width, adjust only the height 
         of the drawing to keep the aspect ratio.
      */
      drawing_area.style.height =
        "" + Math.trunc( drawing_area.clientWidth / ASPECT_RATIO ) + "px";
   }
   else   /* Desktop */
   {
      let start_width;
      if( span < MIN_WIDTH )
        start_width = MIN_WIDTH;
      else if( span > MAX_WIDTH )
        start_width = MAX_WIDTH;
      else
        start_width = span;

      let size_coeff = this.SetDrawingSize.size_array[ this.SizeIndex ];
      let width = Math.trunc( Math.max( start_width * size_coeff, MIN_WIDTH ) );
      drawing_area.style.width = "" + width + "px";
      drawing_area.style.height = 
        "" + Math.trunc( width / ASPECT_RATIO ) + "px";
   }

   // Adjust canvas resolution for mobile devices and browser zoom state.
   if( !next_size )
     this.SetCanvasResolution();
}

//////////////////////////////////////////////////////////////////////////////
// Increases canvas resolution for mobile devices with HiDPI displays and for
// browser zooming. Sets CoordScale global variable.
//////////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetCanvasResolution = function()
{
   let TextScale, PixelOffsetScale, ScreenCoordScale, NativeWidgetTextScale;

   if( this.IsMobile )
   {
      /* CoordScale parameter defines canvas coordinate scaling.
         Values greater than 1 increase canvas resolution and result in 
         sharper rendering. On mobile devices with devicePixelRatio > 1,
         the value of devicePixelRatio may be used for very crisp rendering
         with very thin lines. For pixel ration greater than 2, limit 
         CoordScale to 2 to draw thicker lines.

         CanvasScale > 1 makes text smaller. The TextScale parameter defines
         the text scaling factor used to increase text size.

         The ScreenCoordScale parameter specifies a scaling factor for fixed
         scale viewports that use screen coordinates. If the size of the fixed
         scale viewport (such as a height of a toolbar) is controlled by a pixel
         offset transformation, ScreenCoordScale may be set to the same value as
         PixelOffsetScale to scale the content of the viewport proportionally
         to its height increase.

         The NativeWidgetTextScale parameter defines the scaling factor that is
         used to scale down text in native widgets (such as native buttons, 
         toggles, etc.) to match the scale of the drawing.
      */
      if( window.devicePixelRatio > 2. )
        this.CoordScale = 2.;
      else
        this.CoordScale = window.devicePixelRatio;
      
      TextScale = 1.5;
      PixelOffsetScale = TextScale;
      ScreenCoordScale = PixelOffsetScale;
      NativeWidgetTextScale = 0.75;
   }
   else   // Desktop
   {
      /* Change canvas resolution to match browser zoom state. */
      this.CoordScale = window.devicePixelRatio;      
      TextScale = window.devicePixelRatio;
      PixelOffsetScale = window.devicePixelRatio;
      ScreenCoordScale = window.devicePixelRatio;
      NativeWidgetTextScale = 1.;    // Don't change native text size.
   }

   GLG.SetCanvasScale( this.CoordScale, TextScale, NativeWidgetTextScale,
                       PixelOffsetScale, ScreenCoordScale );
}

//////////////////////////////////////////////////////////////////////////
function RemoveElement( parent_id, child_id )
{
   let element;
   if( parent_id == null )
     // No parent was provided: remove from document.
     element = document.getElementById( child_id );
   else
   {
      // Parent ID was provided: remove from parent.
      let parent = document.getElementById( parent_id );
      if( parent == null )
        return;
      element = parent.querySelector( "#" + child_id );
   }
   
   if( element != null )
     element.parentNode.removeChild( element );
}

//////////////////////////////////////////////////////////////////////////
export function ShowElement( name, state )
{
   let element = document.getElementById( name );
   if( element != null )
     element.style.display = ( state ? "inline-block" : "none" );
}

//////////////////////////////////////////////////////////////////////////////
// Changes action to be performed on touch move on mobile devices.
//////////////////////////////////////////////////////////////////////////////
GlgGIS.prototype.SetTouchAction = function()
{
   let scroll_mode_elem =
     document.getElementById( this.GLG_div_name + "_scroll_mode" );
   this.ScrollMode = scroll_mode_elem.checked;
}

//////////////////////////////////////////////////////////////////////////////
function Debug( message )
{
   if( DEBUG )
     console.log( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppAlert( message )
{
   window.alert( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppError( message )
{
   console.error( message );
}

//////////////////////////////////////////////////////////////////////////////
function NodeData( name, lon, lat )
{
   this.name = name;      /* String */
   this.lat_lon = GLG.CreateGlgPoint( lon, lat, 0 );   /* GlgPoint */
   this.graphics = new Array( 2 );  /* GlgObject[2] */
}

//////////////////////////////////////////////////////////////////////////////
function PlaneData()
{
   this.name = null;                /* String */
   this.lat_lon = GLG.CreateGlgPoint( 0, 0, 0 ); /* GlgPoint */
   this.flight_number = 0;          /* int */
   this.tooltip = new Array( 2 );   /* String[2] */
   this.graphics = new Array( 2 );  /* GlgObject[2] */
   this.from_node = null;           /* NodeData */
   this.to_node = null;             /* NodeData */
   this.path_position = 0;          /* double */
   this.path_position_last = 0;     /* double */
   this.speed = 0;                  /* double */
   this.has_angle = new Array( 2 ); /* boolean[2] */
   this.angle = new Array( 2 );     /* double[2] */
}
